import { useEffect, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { Target, X } from 'lucide-react';

cytoscape.use(dagre);

const SEVERITY_STYLES = {
  critical: { bg: '#dc2626', border: '#991b1b', glow: 'rgba(220,38,38,0.5)' },
  high: { bg: '#ea580c', border: '#c2410c', glow: 'rgba(234,88,12,0.4)' },
  medium: { bg: '#eab308', border: '#ca8a04', glow: 'rgba(234,179,8,0.3)' },
  low: { bg: '#3b82f6', border: '#2563eb', glow: 'rgba(59,130,246,0.3)' },
  info: { bg: '#6b7280', border: '#4b5563', glow: 'rgba(107,114,128,0.2)' },
  target: { bg: '#7c3aed', border: '#6d28d9', glow: 'rgba(124,58,237,0.5)' },
  impact: { bg: '#f43f5e', border: '#e11d48', glow: 'rgba(244,63,94,0.5)' },
};

interface AttackPathGraphProps {
  findings: any[];
  attackChains: any[];
  target: string;
}

export default function AttackPathGraph({ findings, attackChains, target }: AttackPathGraphProps): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [pathInfo, setPathInfo] = useState<{ count: number; shortest: number } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const actionable = findings.filter(f => f.severity !== 'info');
    if (actionable.length === 0) return;

    const elements: cytoscape.ElementDefinition[] = [];

    // Entry point node
    elements.push({
      data: { id: 'target', label: target, type: 'target', severity: 'target' },
    });

    // Finding nodes -- deduplicate by scan_type, keep highest severity per type
    const byType: Record<string, any> = {};
    for (const f of actionable) {
      const key = f.scan_type || 'unknown';
      if (!byType[key] || severityRank(f.severity) < severityRank(byType[key].severity)) {
        byType[key] = f;
      }
    }

    Object.entries(byType).forEach(([type, f]) => {
      const nodeId = `finding-${type}`;
      elements.push({
        data: {
          id: nodeId,
          label: (f.title || '').substring(0, 40),
          sublabel: type,
          type: 'finding',
          severity: f.severity,
          finding: f,
        },
      });
      // Edge from target to finding
      elements.push({
        data: {
          id: `e-target-${nodeId}`,
          source: 'target',
          target: nodeId,
          weight: severityWeight(f.severity),
        },
      });
    });

    // Impact nodes from attack chains
    attackChains.forEach((chain, i) => {
      const impactId = `impact-${i}`;
      elements.push({
        data: {
          id: impactId,
          label: (chain.title || '').substring(0, 45),
          sublabel: chain.data_at_risk || '',
          type: 'impact',
          severity: 'impact',
          chain,
        },
      });
      // Connect chain steps to impact
      if (chain.steps) {
        for (const step of chain.steps) {
          const sourceId = `finding-${step.module}`;
          if (byType[step.module]) {
            elements.push({
              data: {
                id: `e-${sourceId}-${impactId}-${step.step}`,
                source: sourceId,
                target: impactId,
                label: `Step ${step.step}`,
                weight: 1,
              },
            });
          }
        }
      }
    });

    // If no chains, create generic impact nodes from critical findings
    if (attackChains.length === 0) {
      const criticals = actionable.filter(f => f.severity === 'critical');
      criticals.forEach((f, i) => {
        const impactId = `impact-generic-${i}`;
        const sourceId = `finding-${f.scan_type}`;
        elements.push({
          data: {
            id: impactId,
            label: 'Impact: ' + (f.title || '').substring(0, 30),
            type: 'impact',
            severity: 'impact',
          },
        });
        elements.push({
          data: {
            id: `e-${sourceId}-${impactId}`,
            source: sourceId,
            target: impactId,
            weight: 1,
          },
        });
      });
    }

    // Create Cytoscape instance
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        // Node base style
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'font-size': '9px',
            'font-weight': 'bold',
            'color': '#ffffff',
            'text-wrap': 'wrap',
            'text-max-width': '120px',
            'width': 50,
            'height': 50,
            'border-width': 3,
            'text-outline-color': '#000000',
            'text-outline-width': 1,
          },
        },
        // Target node
        {
          selector: 'node[type="target"]',
          style: {
            'background-color': SEVERITY_STYLES.target.bg,
            'border-color': SEVERITY_STYLES.target.border,
            'width': 70,
            'height': 70,
            'font-size': '11px',
            'shape': 'diamond',
          },
        },
        // Finding nodes by severity
        ...(['critical', 'high', 'medium', 'low'] as const).map(sev => ({
          selector: `node[severity="${sev}"]`,
          style: {
            'background-color': SEVERITY_STYLES[sev].bg,
            'border-color': SEVERITY_STYLES[sev].border,
            'shape': sev === 'critical' ? ('hexagon' as const) : ('roundrectangle' as const),
            'width': sev === 'critical' ? 60 : 50,
            'height': sev === 'critical' ? 60 : 50,
          },
        })),
        // Impact node
        {
          selector: 'node[type="impact"]',
          style: {
            'background-color': SEVERITY_STYLES.impact.bg,
            'border-color': SEVERITY_STYLES.impact.border,
            'width': 65,
            'height': 65,
            'shape': 'star',
            'font-size': '10px',
          },
        },
        // Edges
        {
          selector: 'edge',
          style: {
            'width': 2,
            'line-color': '#475569',
            'target-arrow-color': '#475569',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 1.2,
            'label': 'data(label)',
            'font-size': '7px',
            'color': '#94a3b8',
            'text-outline-color': '#0f172a',
            'text-outline-width': 1,
          },
        },
        // Critical path animated dashes
        {
          selector: 'edge[weight=1]',
          style: {
            'line-style': 'dashed',
            'line-dash-pattern': [6, 3],
            'line-color': '#64748b',
            'target-arrow-color': '#64748b',
          },
        },
        // Highlighted path
        {
          selector: '.highlighted',
          style: {
            'line-color': '#f43f5e',
            'target-arrow-color': '#f43f5e',
            'width': 4,
            'z-index': 999,
          },
        },
        {
          selector: 'node.highlighted',
          style: {
            'border-width': 5,
            'border-color': '#f43f5e',
          },
        },
      ],
      layout: {
        name: 'dagre',
        rankDir: 'TB',
        nodeSep: 60,
        rankSep: 100,
        padding: 30,
      } as any,
      wheelSensitivity: 0.3,
    });

    // Click handler -- highlight shortest path from target
    cy.on('tap', 'node', (evt) => {
      const node = evt.target;
      const data = node.data();
      setSelectedNode(data);

      cy.elements().removeClass('highlighted');
      if (data.type === 'impact' || data.type === 'finding') {
        const dijkstra = cy.elements().dijkstra({
          root: '#target',
          // WeightFn de @types/cytoscape declara EdgeCollection aunque en
          // runtime llega un edge individual. .data() existe en ambos.
          weight: (edge: cytoscape.EdgeCollection) => edge.data('weight') || 1,
        });
        const path = dijkstra.pathTo(node);
        path.addClass('highlighted');
      }
    });

    // Background click clears selection
    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        setSelectedNode(null);
        cy.elements().removeClass('highlighted');
      }
    });

    // Compute graph metrics
    const allPaths = computePathCount(cy);
    setPathInfo(allPaths);

    cyRef.current = cy;
    return () => {
      cy.destroy();
    };
  }, [findings, attackChains, target]);

  // Don't render if no actionable findings
  if (findings.filter(f => f.severity !== 'info').length === 0) {
    return null;
  }

  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-gray-700 print:hidden"
      style={{ background: '#0f172a' }}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-red-400" />
          <h3 className="text-sm font-bold text-white">Attack Path Graph</h3>
          {pathInfo && pathInfo.count > 0 && (
            <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full">
              {pathInfo.count} path{pathInfo.count !== 1 ? 's' : ''} &middot; shortest: {pathInfo.shortest} step{pathInfo.shortest !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-[9px]">
          {[
            { label: 'Target', color: SEVERITY_STYLES.target.bg, shape: '◆' },
            { label: 'Critical', color: SEVERITY_STYLES.critical.bg, shape: '⬡' },
            { label: 'High', color: SEVERITY_STYLES.high.bg, shape: '■' },
            { label: 'Medium', color: SEVERITY_STYLES.medium.bg, shape: '■' },
            { label: 'Impact', color: SEVERITY_STYLES.impact.bg, shape: '★' },
          ].map(l => (
            <span key={l.label} className="flex items-center gap-1 text-gray-400">
              <span style={{ color: l.color }}>{l.shape}</span> {l.label}
            </span>
          ))}
        </div>
      </div>

      {/* Graph container */}
      <div ref={containerRef} style={{ height: '500px', width: '100%' }} />

      {/* Zoom controls */}
      <div className="absolute bottom-4 left-4 flex flex-col gap-1">
        <button
          onClick={() => cyRef.current?.zoom(cyRef.current.zoom() * 1.3)}
          className="w-8 h-8 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700 text-sm font-bold"
          title="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => cyRef.current?.zoom(cyRef.current.zoom() / 1.3)}
          className="w-8 h-8 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700 text-sm font-bold"
          title="Zoom out"
        >
          &minus;
        </button>
        <button
          onClick={() => cyRef.current?.fit(undefined, 30)}
          className="w-8 h-8 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700 text-[9px] font-bold"
          title="Fit to view"
        >
          FIT
        </button>
      </div>

      {/* Selected node detail panel */}
      {selectedNode && (
        <div className="absolute top-16 right-4 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 max-h-[400px] overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span
                className="px-2 py-0.5 rounded text-xs font-bold text-white"
                style={{
                  background:
                    SEVERITY_STYLES[selectedNode.severity as keyof typeof SEVERITY_STYLES]?.bg || '#6b7280',
                }}
              >
                {(selectedNode.severity || '').toUpperCase()}
              </span>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-500 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <h4 className="font-bold text-sm text-white">{selectedNode.label}</h4>
            {selectedNode.sublabel && (
              <p className="text-xs text-gray-400 mt-1">{selectedNode.sublabel}</p>
            )}
            {selectedNode.finding && (
              <>
                <p className="text-xs text-gray-300 mt-2">
                  {selectedNode.finding.description?.substring(0, 200)}
                </p>
                {selectedNode.finding.evidence && (
                  <div className="mt-2 p-2 bg-gray-800 rounded text-[10px] font-mono text-gray-400 break-all">
                    {selectedNode.finding.evidence.substring(0, 150)}
                  </div>
                )}
                {selectedNode.finding.remediation && (
                  <div className="mt-2 border-t border-gray-700 pt-2">
                    <p className="text-[10px] text-green-400 font-bold mb-1">Remediation</p>
                    <p className="text-[10px] text-gray-300">
                      {selectedNode.finding.remediation.substring(0, 200)}
                    </p>
                  </div>
                )}
              </>
            )}
            {selectedNode.chain && (
              <div className="mt-2 space-y-1">
                <p className="text-[10px] text-red-400 font-bold">Attack Chain Steps</p>
                {selectedNode.chain.steps?.map((s: any, i: number) => (
                  <div key={i} className="text-[10px] text-gray-300">
                    <span className="text-purple-400 font-bold">{s.step}.</span>{' '}
                    {s.finding?.substring(0, 50)}
                  </div>
                ))}
                {selectedNode.chain.data_at_risk && (
                  <p className="text-[10px] text-red-300 mt-1">
                    Data at risk: {selectedNode.chain.data_at_risk}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function severityRank(sev: string): number {
  const ranks: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  return ranks[sev] ?? 5;
}

function severityWeight(sev: string): number {
  const weights: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 5, info: 10 };
  return weights[sev] ?? 10;
}

function computePathCount(cy: cytoscape.Core): { count: number; shortest: number } {
  const impactNodes = cy.nodes('[type="impact"]');
  const targetNode = cy.$('#target');
  let totalPaths = 0;
  let shortestPath = Infinity;

  impactNodes.forEach(impact => {
    const dijkstra = cy.elements().dijkstra({
      root: targetNode,
      // Ver nota arriba: el tipo declarado es EdgeCollection.
      weight: (e: cytoscape.EdgeCollection) => e.data('weight') || 1,
    });
    const path = dijkstra.pathTo(impact);
    if (path.length > 0) {
      totalPaths++;
      const pathLength = path.edges().length;
      if (pathLength < shortestPath) {
        shortestPath = pathLength;
      }
    }
  });

  return { count: totalPaths, shortest: shortestPath === Infinity ? 0 : shortestPath };
}
